import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { DATE_FORMATS, DATE_PICKER_LOCALE } from 'src/app/shared/constants';
import { DateFormat, FilterType } from 'src/app/shared/enums';
import { IColumnConfig } from 'src/app/shared/interfaces/ITable';
import { formatDateOrTime, isEmptyValue } from 'src/app/shared/utils';

const VISIBLE_FILTER_CHIP_LIMIT = 3;
const OVERFLOW_CLOSE_DELAY_MS = 120;

interface IFilterChip {
  key: string;
  value: any;
  label: string;
}

interface IFilterChipGroup {
  key: string;
  title: string;
  chips: (IFilterChip & { displayValue: string })[];
}

@Component({
  selector: 'app-filter-table-v2',
  standalone: true,
  templateUrl: './filter-table-v2.component.html',
  styleUrls: ['./filter-table-v2.component.scss'],
  imports: [FormsModule, IconModule, MaterialModule, TranslateModule],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: DATE_PICKER_LOCALE }
  ]
})
export class FilterTableV2Component {
  @Input() columnConfig: IColumnConfig[] = [];
  @Input() set initialFilterValues(value: Record<string, unknown> | null) {
    this.filterValues = this.cloneFilterRecord(value ?? {});
    if (!this.menuOpen) this.syncTextDrafts();
  }

  @Output() filterChange = new EventEmitter<Record<string, unknown>>();

  filterValues: Record<string, any> = {};
  textDrafts: Record<string, string> = {};
  expandedKey: string | null = null;
  menuOpen = false;
  overflowOpen = false;
  private overflowCloseTimer?: ReturnType<typeof setTimeout>;
  private filterChipsCache: IFilterChip[] = [];
  private filterChipsCacheValues?: Record<string, any>;
  private filterChipsCacheColumns?: IColumnConfig[];
  private filterChipsCacheLanguage?: string;
  private filterChipsCacheDataSourceSignature?: string;
  private hiddenFilterGroupsSource?: IFilterChip[];
  private hiddenFilterGroupsCache: IFilterChipGroup[] = [];

  constructor(private translate: TranslateService) {}

  get filterableColumns(): IColumnConfig[] {
    return this.columnConfig.filter(column => this.isFilterableColumn(column));
  }

  get filterChips(): IFilterChip[] {
    const language = this.translate.currentLang;
    const dataSourceSignature = this.getFilterDataSourceSignature();
    if (
      this.filterChipsCacheValues === this.filterValues &&
      this.filterChipsCacheColumns === this.columnConfig &&
      this.filterChipsCacheLanguage === language &&
      this.filterChipsCacheDataSourceSignature === dataSourceSignature
    ) {
      return this.filterChipsCache;
    }

    this.filterChipsCache = this.filterableColumns.flatMap(column => {
      const value = this.filterValues[column.key];
      if (!this.hasValidValue(column)) return [];
      const title = this.getTranslatedColumnTitle(column);
      if (this.getFilterType(column) === FilterType.MULTI_SELECT && Array.isArray(value)) {
        return value.map(item => ({
          key: column.key,
          value: item,
          label: `${title}: ${this.getOptionLabelByValue(column, item)}`
        }));
      }
      return [{ key: column.key, value, label: `${title}: ${this.getFilterDisplayValue(column)}` }];
    });
    this.filterChipsCacheValues = this.filterValues;
    this.filterChipsCacheColumns = this.columnConfig;
    this.filterChipsCacheLanguage = language;
    this.filterChipsCacheDataSourceSignature = dataSourceSignature;
    return this.filterChipsCache;
  }

  get activeFilterCount(): number {
    return this.filterableColumns.filter(column => this.hasValidValue(column)).length;
  }

  get visibleFilterChips(): IFilterChip[] {
    return this.filterChips.slice(0, VISIBLE_FILTER_CHIP_LIMIT);
  }

  get hiddenFilterChipCount(): number {
    return Math.max(this.filterChips.length - this.visibleFilterChips.length, 0);
  }

  get hiddenFilterGroups(): IFilterChipGroup[] {
    const filterChips = this.filterChips;
    if (filterChips === this.hiddenFilterGroupsSource) return this.hiddenFilterGroupsCache;

    const hiddenChips = filterChips.slice(VISIBLE_FILTER_CHIP_LIMIT);

    const groups = new Map<string, IFilterChipGroup>();

    hiddenChips.forEach(chip => {
      const column = this.filterableColumns.find(item => item.key === chip.key);
      if (!column) return;

      const group = groups.get(chip.key) ?? {
        key: chip.key,
        title: this.getTranslatedColumnTitle(column),
        chips: []
      };
      group.chips.push({
        ...chip,
        displayValue: this.getFilterType(column) === FilterType.MULTI_SELECT
          ? this.getOptionLabelByValue(column, chip.value)
          : this.getFilterDisplayValue(column)
      });
      groups.set(chip.key, group);
    });

    this.hiddenFilterGroupsSource = filterChips;
    this.hiddenFilterGroupsCache = Array.from(groups.values());
    return this.hiddenFilterGroupsCache;
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.overflowOpen) this.closeOverflow();
    else if (this.menuOpen) this.closeMenu();
  }

  isFilterableColumn(column: IColumnConfig): boolean {
    return !!column.filterable && column.filterable !== FilterType.NONE && column.filterable !== FilterType.RESET;
  }

  isSelectFilter(column: IColumnConfig): boolean {
    const type = this.getFilterType(column);
    return type === FilterType.SELECT || type === FilterType.MULTI_SELECT || type === FilterType.BOOLEAN;
  }

  isDateFilter(column: IColumnConfig): boolean {
    return this.getFilterType(column) === FilterType.DATE;
  }

  getFilterType(column: IColumnConfig): FilterType {
    return column.filterable ?? FilterType.TEXT;
  }

  getColumnTitle(column: IColumnConfig): string {
    if (column.filterTitle) return column.filterTitle;
    return typeof column.title === 'function' ? column.title(column) : (column.title ?? column.key);
  }

  getTranslatedColumnTitle(column: IColumnConfig): string {
    const title = this.getColumnTitle(column);
    return column.disableTitleTranslation ? title : this.translate.instant(title);
  }

  toggleMenu() {
    this.menuOpen ? this.closeMenu() : this.openMenu();
  }

  openMenu() {
    this.closeOverflow();
    this.menuOpen = true;
    this.expandedKey = null;
    this.syncTextDrafts();
  }

  closeMenu() {
    // Discard unapplied text drafts when closing without Enter.
    this.syncTextDrafts();
    this.menuOpen = false;
    this.expandedKey = null;
  }

  openOverflow() {
    if (this.overflowCloseTimer) {
      clearTimeout(this.overflowCloseTimer);
      this.overflowCloseTimer = undefined;
    }
    this.closeMenu();
    this.overflowOpen = true;
  }

  closeOverflow() {
    if (this.overflowCloseTimer) {
      clearTimeout(this.overflowCloseTimer);
      this.overflowCloseTimer = undefined;
    }
    this.overflowOpen = false;
  }

  scheduleOverflowClose() {
    if (this.overflowCloseTimer) clearTimeout(this.overflowCloseTimer);
    this.overflowCloseTimer = setTimeout(() => this.closeOverflow(), OVERFLOW_CLOSE_DELAY_MS);
  }

  viewAllFilters() {
    this.closeOverflow();
    this.openMenu();
  }

  onPanelKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.closeMenu();
    }
  }

  toggleField(column: IColumnConfig) {
    this.expandedKey = this.expandedKey === column.key ? null : column.key;
  }

  onTextDraftChange(key: string, rawValue: string) {
    this.textDrafts[key] = rawValue ?? '';
  }

  onTextEnter(key: string) {
    this.applyTextFilter(key, this.textDrafts[key] ?? '');
  }

  onTextBlur(key: string) {
    this.applyTextFilter(key, this.textDrafts[key] ?? '');
  }

  clearTextFilter(key: string, event: Event) {
    event.stopPropagation();
    this.textDrafts[key] = '';
    this.setFilterValue(key, undefined);
  }

  onDateChange(key: string, value: any): void {
    const normalizedValue = typeof value?.toDate === 'function' ? value.toDate() : value;
    this.setFilterValue(key, normalizedValue ?? undefined);
  }

  clearDateFilter(key: string, event: Event): void {
    event.stopPropagation();
    this.setFilterValue(key, undefined);
  }

  setFilterValue(key: string, value: unknown) {
    const nextValue = value === undefined || value === null || value === '' ? undefined : value;
    const currentValue = this.filterValues[key];
    const same =
      Array.isArray(nextValue) && Array.isArray(currentValue)
        ? nextValue.length === currentValue.length && nextValue.every((item, index) => item === currentValue[index])
        : nextValue === currentValue;

    if (same || (nextValue === undefined && !(key in this.filterValues))) {
      return;
    }

    const nextFilterValues = this.cloneFilterRecord(this.filterValues);
    if (nextValue === undefined) delete nextFilterValues[key];
    else nextFilterValues[key] = nextValue;
    this.filterValues = nextFilterValues;

    this.emitFilterChange();
  }

  isOptionChecked(column: IColumnConfig, option: any): boolean {
    const value = this.filterValues[column.key];
    const optionValue = this.getOptionValue(column, option);
    if (this.getFilterType(column) === FilterType.BOOLEAN) {
      return this.isSameOptionValue(value, optionValue);
    }
    return this.getFilterType(column) === FilterType.MULTI_SELECT
      ? Array.isArray(value) && value.some(item => this.isSameOptionValue(item, optionValue))
      : this.isSameOptionValue(value, optionValue);
  }

  onOptionChange(column: IColumnConfig, option: any, checked: boolean) {
    const optionValue = this.getOptionValue(column, option);
    if (this.getFilterType(column) === FilterType.MULTI_SELECT) {
      const current = Array.isArray(this.filterValues[column.key]) ? [...this.filterValues[column.key]] : [];
      this.setFilterValue(column.key, checked ? [...current, optionValue] : current.filter(item => !this.isSameOptionValue(item, optionValue)));
    } else {
      this.setFilterValue(column.key, checked ? optionValue : undefined);
    }
  }

  toggleOption(column: IColumnConfig, option: any): void {
    this.onOptionChange(column, option, !this.isOptionChecked(column, option));
  }

  getFilterSummary(column: IColumnConfig): string {
    const value = this.filterValues[column.key];
    if (!this.hasValidValue(column)) return this.translate.instant('all');

    if (this.getFilterType(column) === FilterType.MULTI_SELECT && Array.isArray(value)) {
      if (value.length === 1) return this.getOptionLabelByValue(column, value[0]);
      return this.translate.instant('n selected', { count: value.length });
    }

    return this.getFilterDisplayValue(column);
  }

  getOptionLabelByValue(column: IColumnConfig, value: any): string {
    const option = this.getSuggestionData(column).find(item => this.isSameOptionValue(this.getOptionValue(column, item), value));
    return option ? this.getOptionLabel(column, option) : String(value ?? '');
  }

  removeFilterByKey(key: string, value: any) {
    const column = this.columnConfig.find(item => item.key === key);
    if (column && this.getFilterType(column) === FilterType.MULTI_SELECT && Array.isArray(this.filterValues[key])) {
      this.setFilterValue(key, this.filterValues[key].filter((item: any) => !this.isSameOptionValue(item, value)));
    } else {
      this.setFilterValue(key, undefined);
      this.textDrafts[key] = '';
    }
  }

  removeHiddenFilter(key: string, value: any) {
    const shouldClose = this.hiddenFilterChipCount <= 1;
    this.removeFilterByKey(key, value);
    if (shouldClose) this.closeOverflow();
  }

  clearHiddenFilters() {
    const hiddenChips = this.filterChips.slice(VISIBLE_FILTER_CHIP_LIMIT);
    const nextValues = this.cloneFilterRecord(this.filterValues);

    hiddenChips.forEach(chip => {
      const column = this.columnConfig.find(item => item.key === chip.key);
      if (column && this.getFilterType(column) === FilterType.MULTI_SELECT && Array.isArray(nextValues[chip.key])) {
        nextValues[chip.key] = nextValues[chip.key].filter((item: any) => !this.isSameOptionValue(item, chip.value));
        if (!nextValues[chip.key].length) delete nextValues[chip.key];
      } else {
        delete nextValues[chip.key];
      }
    });

    this.filterValues = nextValues;
    this.syncTextDrafts();
    this.closeOverflow();
    this.emitFilterChange();
  }

  clearAllFilters() {
    this.filterValues = {};
    this.textDrafts = {};
    this.closeOverflow();
    this.emitFilterChange();
  }

  getSuggestionData(column: IColumnConfig): any[] {
    if (this.getFilterType(column) === FilterType.BOOLEAN) {
      return [
        { id: true, name: this.translate.instant('yes') },
        { id: false, name: this.translate.instant('no') }
      ];
    }
    return column.filterDataSource || [];
  }

  getFilterDisplayValue(column: IColumnConfig): string {
    const value = this.filterValues[column.key];
    if (isEmptyValue(value)) return '';

    const filterType = this.getFilterType(column);

    if (filterType === FilterType.BOOLEAN) {
      return this.translate.instant(value === true || value === 'true' || value === 1 ? 'yes' : 'no');
    }

    if (filterType === FilterType.DATE_RANGE) {
      if (value.start && value.end) {
        return `${this.formatDate(value.start)} - ${this.formatDate(value.end)}`;
      }
      if (value.start) return `${this.translate.instant('from')} ${this.formatDate(value.start)}`;
      if (value.end) return `${this.translate.instant('to')} ${this.formatDate(value.end)}`;
      return '';
    }

    if (filterType === FilterType.DATE) {
      return this.formatDate(value);
    }

    if (filterType === FilterType.SELECT) {
      const option = this.getSuggestionData(column).find(item => this.isSameOptionValue(this.getOptionValue(column, item), value));
      return option ? this.getOptionLabel(column, option) : value;
    }

    if (filterType === FilterType.MULTI_SELECT && Array.isArray(value)) {
      const labels = value.map(item => this.getOptionLabelByValue(column, item));
      if (labels.length > 2) {
        return `${labels.slice(0, 2).join(', ')} (+${labels.length - 2})`;
      }
      return labels.join(', ');
    }

    return value;
  }

  getOptionLabel(column: IColumnConfig, option: any): string {
    const labelKey = column.selectionConfig?.title || 'name';
    const label = option?.[labelKey] || option?.label || option;
    return column.translateFilterOptions ? this.translate.instant(String(label)) : label;
  }

  getOptionValue(column: IColumnConfig, option: any): any {
    const valueKey = column.selectionConfig?.key || 'id';
    return option?.[valueKey] ?? option?.value ?? option;
  }

  hasValidValue(column: IColumnConfig): boolean {
    const value = this.filterValues[column.key];
    if (isEmptyValue(value)) return false;
    if (this.getFilterType(column) === FilterType.DATE_RANGE && !value?.start && !value?.end) {
      return false;
    }
    return true;
  }

  emitFilterChange() {
    const output = Object.fromEntries(
      Object.entries(this.filterValues).flatMap(([key, value]) => {
        const column = this.columnConfig.find(item => item.key === key);
        return column && this.hasValidValue(column) ? [[key, value]] : [];
      })
    );
    this.filterChange.emit(output);
  }

  formatDate(date: Date | string): string {
    return formatDateOrTime(date as any, DateFormat.DATE);
  }

  private applyTextFilter(key: string, rawValue: string) {
    const value = rawValue?.trim() || undefined;
    this.textDrafts[key] = value ?? '';
    this.setFilterValue(key, value);
  }

  private syncTextDrafts() {
    const drafts: Record<string, string> = {};
    this.filterableColumns.forEach(column => {
      if (!this.isSelectFilter(column) && !this.isDateFilter(column)) {
        const value = this.filterValues[column.key];
        drafts[column.key] = value == null ? '' : String(value);
      }
    });
    this.textDrafts = drafts;
  }

  private cloneFilterRecord(value: Record<string, unknown>): Record<string, any> {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => {
        if (Array.isArray(item)) return [key, [...item]];
        if (item instanceof Date) return [key, new Date(item)];
        if (item && typeof item === 'object') return [key, { ...(item as Record<string, unknown>) }];
        return [key, item];
      })
    );
  }

  private getFilterDataSourceSignature(): string {
    return this.filterableColumns.map(column => `${column.key}:${column.filterDataSource?.length ?? 0}`)
                                 .join('|');
  }

  private isSameOptionValue(left: unknown, right: unknown): boolean {
    return left === right || String(left) === String(right);
  }
}
